import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Shield } from 'lucide-react';
import { AppLang, t } from '../i18n';
import './LoadingScreen.css';

interface LoadingScreenProps {
  lang: AppLang;
  onLoadingComplete: () => void;
}

export const LoadingScreen = ({ lang, onLoadingComplete }: LoadingScreenProps) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(onLoadingComplete, 400);
          return 100;
        }
        return prev + 10;
      });
    }, 180);

    return () => clearInterval(interval);
  }, [onLoadingComplete]);

  return (
    <motion.div
      className="ls"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Mesh gradient background */}
      <div className="ls-bg" />

      {/* Animated orb */}
      <div className="ls-orb">
        {/* Radar sweep */}
        <div className="ls-radar" />

        {/* Orbit rings */}
        <div className="ls-orbit ls-orbit--1">
          <div className="ls-dot" style={{ top: '-3px', left: '50%' }} />
          <div className="ls-dot" style={{ bottom: '-3px', left: '50%' }} />
        </div>
        <div className="ls-orbit ls-orbit--2">
          <div className="ls-dot" style={{ top: '15%', right: '-3px' }} />
          <div className="ls-dot" style={{ bottom: '15%', left: '-3px' }} />
        </div>
        <div className="ls-orbit ls-orbit--3">
          <div className="ls-dot" style={{ top: '50%', right: '-3px' }} />
          <div className="ls-dot" style={{ bottom: '-3px', left: '30%' }} />
        </div>

        {/* Floating particles */}
        <div className="ls-particle ls-particle--1" />
        <div className="ls-particle ls-particle--2" />
        <div className="ls-particle ls-particle--3" />
        <div className="ls-particle ls-particle--4" />

        {/* Pulse rings */}
        <div className="ls-pulse" />
        <div className="ls-pulse ls-pulse--2" />
        <div className="ls-pulse ls-pulse--3" />

        {/* Center glass circle */}
        <motion.div
          className="ls-center"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
        >
          <div className="ls-center-border" />
          <motion.div
            className="ls-center-icon"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.35, type: 'spring', stiffness: 150 }}
          >
            <img src="/logo.png?v=3" width={66} height={66} className="ls-logo-img" alt="PATRiK VPN" style={{ objectFit: 'contain' }} />
          </motion.div>
        </motion.div>
      </div>

      {/* Title + progress */}
      <motion.div
        className="ls-info"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h2 className="ls-title">PATRiK VPN</h2>

        <div className="ls-progress-track">
          <motion.div
            className="ls-progress-fill"
            initial={{ width: '0%' }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.2 }}
          />
        </div>

        <p className="ls-status">
          {progress < 30 && t(lang, 'loading')}
          {progress >= 30 && progress < 60 && t(lang, 'connecting')}
          {progress >= 60 && progress < 90 && t(lang, 'almost_ready')}
          {progress >= 90 && t(lang, 'ready')}
        </p>
      </motion.div>

      <motion.div
        className="ls-footer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        transition={{ delay: 0.8 }}
      >
        PATRiK VPN
      </motion.div>
    </motion.div>
  );
};
